import { oc } from "@orpc/contract";
import { z } from "zod";
import {
  ColumnConfigEntrySchema,
  ColumnSortingSchema,
} from "@erp/shared-types";

// Shared with task-page default columns; `format` is optional so older
// saved configs stay valid.
export const ColumnConfigSchema = ColumnConfigEntrySchema;

export const GetColumnConfigInputSchema = z.object({
  tableId: z.string().min(1),
});

export const GetColumnConfigResponseSchema = z
  .object({
    columns: z.array(ColumnConfigSchema),
    /** Saved sort, in priority order; absent when this layout never set one. */
    sorting: ColumnSortingSchema.optional(),
  })
  .nullable();

export const UpsertColumnConfigInputSchema = z.object({
  tableId: z.string().min(1),
  columns: z.array(ColumnConfigSchema),
  /**
   * Omit to keep the saved sort as it is; `null` clears it so the layout
   * falls back to the default sort.
   */
  sorting: ColumnSortingSchema.nullable().optional(),
});

export const UpsertColumnConfigResponseSchema = z.object({
  columns: z.array(ColumnConfigSchema),
  sorting: ColumnSortingSchema.optional(),
});

export const userTableColumnConfigsContracts = {
  get: oc
    .input(GetColumnConfigInputSchema)
    .output(GetColumnConfigResponseSchema)
    .route({
      method: "GET",
      summary: "Get my table column layout",
      description:
        "Returns the signed-in user's saved column layout (and sort, when saved) for one table, or null when none is saved.",
    }),
  upsert: oc
    .input(UpsertColumnConfigInputSchema)
    .output(UpsertColumnConfigResponseSchema)
    .route({
      method: "POST",
      summary: "Save my table column layout",
      description:
        "Saves the signed-in user's column visibility, order and formatting for one table, replacing any previous layout. The saved sort changes only when `sorting` is sent.",
    }),
} as const;

export type ColumnConfig = z.infer<typeof ColumnConfigSchema>;
export type GetColumnConfigInput = z.infer<typeof GetColumnConfigInputSchema>;
export type GetColumnConfigResponse = z.infer<
  typeof GetColumnConfigResponseSchema
>;
export type UpsertColumnConfigInput = z.infer<
  typeof UpsertColumnConfigInputSchema
>;
export type UpsertColumnConfigResponse = z.infer<
  typeof UpsertColumnConfigResponseSchema
>;
